import React, { useState, useEffect, useRef } from "react";
import { Modal, Spin, Button, Tooltip } from "antd";
import { DownloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Document, Page, pdfjs } from "react-pdf";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus, solarizedlight, nightOwl, atomDark, duotoneLight, duotoneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import Papa from "papaparse";
import { JSONTree } from "react-json-tree";
import "./styles.scss";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";
import { getLanguageFromExtension } from "utils/utils";
import { NEXUS_PREVIEW_THEME_ENABLE } from "const";
import PreviewToolbar from "./PreviewToolbar";
import HtmlRenderer from "./renderers/HtmlRenderer";
import { getFileType, getFileTypeRegex, getJsonTheme } from "./utils/fileTypes";

interface FilePreviewModalProps {
  open: boolean;
  onClose: () => void;
  fileUrl: string | null;
  fileName: string;
}

const syntaxThemes = [
  { label: "VSCode Dark", value: "vscDarkPlus", style: vscDarkPlus },
  { label: "Solarized Light", value: "solarizedlight", style: solarizedlight },
  { label: "Night Owl", value: "nightOwl", style: nightOwl },
  { label: "Atom Dark", value: "atomDark", style: atomDark },
  { label: "Duotone Light", value: "duotoneLight", style: duotoneLight },
  { label: "Duotone Dark", value: "duotoneDark", style: duotoneDark },
];

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({
  open,
  onClose,
  fileUrl,
  fileName,
}) => {
  const [textContent, setTextContent] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1.0);
  const [selectedTheme, setSelectedTheme] = useState("solarizedlight"); // default to light
  const [csvData, setCsvData] = useState<string[][] | null>(null);
  const [jsonData, setJsonData] = useState<any>(null);
  const [jsonParseError, setJsonParseError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [htmlViewMode, setHtmlViewMode] = useState<"code" | "rendered">("code"); // For HTML files
  const [htmlRenderError, setHtmlRenderError] = useState<string | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const disableThemeSelector = NEXUS_PREVIEW_THEME_ENABLE === "true" ? true : false || false;

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setCurrentPage(1);
  }
  const isTextOrCodeFile = fileName.match(/\.(txt|py|js|jsx|ts|tsx|css|scss|c|cpp|cs|java|md|xml|yml|yaml|sh|sql)$/i);
  const isHtmlFile = fileName.match(/\.html$/i);
  const isCsvFile = fileName.match(/\.csv$/i);
  const isJsonFile = fileName.match(/\.json$/i);
  
  // Fullscreen logic
  useEffect(() => {
    if (isFullscreen && previewRef.current) {
      previewRef.current.requestFullscreen?.();
    } else if (!isFullscreen && document.fullscreenElement) {
      document.exitFullscreen?.();
    }
    // Exit fullscreen if modal closes
    if (!open && document.fullscreenElement) {
      document.exitFullscreen?.();
    }
  }, [isFullscreen, open]);
  
  // Reset HTML states when modal closes or file changes
  useEffect(() => {
    if (!open) {
      setHtmlViewMode("code");
      setHtmlRenderError(null);
      setJsonData(null);
      setJsonParseError(null);
    }
  }, [open, fileName]);

  useEffect(() => {

    if (open && fileUrl && (isTextOrCodeFile || isHtmlFile)) {
      fetch(fileUrl)
        .then((res) => res.text())
        .then((text) => {
          setTextContent(text);
        }).catch(() => {
          setTextContent("Failed to load file.");
          if (isHtmlFile) {
            setHtmlRenderError("Failed to load HTML file.");
          }
        });
      setCsvData(null);
    } else if (open && fileUrl && isCsvFile) {
      fetch(fileUrl)
        .then((res) => res.text())
        .then((text) => {
          const result = Papa.parse<string[]>(text, { skipEmptyLines: true });
          setCsvData(result.data);
        })
        .catch(() => setCsvData(null));
      setTextContent(null);
    } else if (open && fileUrl && isJsonFile) {
      fetch(fileUrl)
        .then((res) => res.text())
        .then((text) => {
          try {
            const parsed = JSON.parse(text);
            setJsonData(parsed);
            setJsonParseError(null);
          } catch (error) {
            setJsonData(null);
            setJsonParseError(error instanceof Error ? error.message : "Invalid JSON format");
          }
        })
        .catch(() => {
          setJsonData(null);
          setJsonParseError("Failed to load JSON file.");
        });
      setCsvData(null);
      setTextContent(null);
    } else {
      setTextContent(null);
      setCsvData(null);
      setJsonData(null);
      setJsonParseError(null);
      setHtmlRenderError(null);
    }
  }, [open, fileUrl, fileName]);

  return (
    <Modal
      title={
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>Preview: {fileName}</span>
          <Tooltip
            placement="rightTop"
            title={
              <div>
                <div><strong>Supported previews</strong></div>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  <li>Images: png, jpg, jpeg, gif</li>
                  <li>HTML (code and rendered)</li>
                  <li>Code: txt, py, js, jsx, ts, tsx, css, scss, c, cpp, cs, java, md, xml, yml, yaml, sh, sql</li>
                  <li>PDF</li>
                  <li>CSV</li>
                  <li>JSON</li>
                </ul>
              </div>
            }
          >
            <Button
              type="text"
              size="small"
              aria-label="Supported previews"
              icon={<InfoCircleOutlined />}
              style={{ marginLeft: 8, color: "#1677ff" }}
            />
          </Tooltip>
          <PreviewToolbar
            fileType={getFileType(fileName)}
            htmlViewMode={htmlViewMode}
            isFullscreen={isFullscreen}
            zoom={zoom}
            selectedTheme={selectedTheme}
            syntaxThemes={syntaxThemes}
            disableThemeSelector={disableThemeSelector}
            onHtmlViewModeChange={setHtmlViewMode}
            onFullscreenToggle={() => setIsFullscreen((prev) => !prev)}
            onZoomChange={setZoom}
            onThemeChange={setSelectedTheme}
            onErrorReset={() => setHtmlRenderError(null)}
          />
        </div>
      }
      open={open}
      onCancel={() => {
        setIsFullscreen(false);
        setHtmlViewMode("code");
        setHtmlRenderError(null);
        setJsonData(null);
        setJsonParseError(null);
        onClose();
      }}
      footer={null}
      width={isFullscreen ? "100vw" : "80%"}
      style={isFullscreen ? { top: 0, padding: 0 } : { top: 10 }}
      bodyStyle={isFullscreen && !isCsvFile ? { height: "100vh", padding: 0 } : {}}
    >
      <div ref={previewRef} style={{
        width: "100%",
        height: isFullscreen && !isCsvFile ? "100vh" : "80vh",
        overflow: "auto",
        background: "#fff",
        borderRadius: 8,
        boxShadow: isFullscreen ? undefined : "0 2px 8px rgba(0,0,0,0.08)",
      }}
      className="file-preview-container"
      >
        {(() => {
          if (!fileUrl) {
            return <Spin />;
          }
          const imageRegex = /\.(png|jpg|jpeg|gif)$/i;
          const htmlRegex = /\.html$/i;
          const codeRegex = /\.(txt|py|js|jsx|ts|tsx|css|scss|c|cpp|cs|java|md|xml|yml|yaml|sh|sql)$/i;
          const pdfRegex = /\.pdf$/i;
          const csvRegex = /\.csv$/i;
          const jsonRegex = /\.json$/i;

          const isImage = imageRegex.exec(fileName);
          const isHtml = htmlRegex.exec(fileName);
          const isCode = codeRegex.exec(fileName);
          const isPdf = pdfRegex.exec(fileName);
          const isCsv = csvRegex.exec(fileName);
          const isJson = jsonRegex.exec(fileName);
          if (isImage) {
            return (
              <img
                src={fileUrl}
                alt={fileName}
                style={{
                  maxWidth: "100%",
                  maxHeight: isFullscreen ? "88vh" : "70vh",
                  display: "block",
                  margin: "0 auto",
                  transform: `scale(${zoom})`,
                  transition: "transform 0.2s",
                }}
              />
            );
          } else if (isHtml) {
            if (textContent) {
              return (
                <div>
                  {htmlRenderError && (
                    <div style={{
                      color: "#ff4d4f",
                      background: "#fff2f0",
                      border: "1px solid #ffccc7",
                      borderRadius: "4px",
                      padding: "12px",
                      margin: "16px",
                      marginBottom: "16px",
                    }}>
                      <strong>HTML Render Error:</strong> {htmlRenderError}
                      <br />
                      <small>Switch to code view to see the raw HTML content.</small>
                    </div>
                  )}
                  <HtmlRenderer
                    textContent={textContent}
                    viewMode={htmlViewMode}
                    zoom={zoom}
                    selectedTheme={selectedTheme}
                    syntaxThemes={syntaxThemes}
                    isFullscreen={isFullscreen}
                    onError={setHtmlRenderError}
                  />
                </div>
              );
            } else {
              return <Spin />;
            }
          } else if (isCode) {
            if (textContent) {
              return (
                <div style={{ transform: `scale(${zoom})`, transformOrigin: "top left", transition: "transform 0.2s", height: "100%" }}>
                  <SyntaxHighlighter
                    language={getLanguageFromExtension(fileName)}
                    style={syntaxThemes.find(t => t.value === selectedTheme)?.style || solarizedlight}
                    showLineNumbers={true}
                    wrapLines={true}
                    customStyle={{
                      maxHeight: "100%",
                      overflow: "auto",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  >
                    {textContent}
                  </SyntaxHighlighter>
                </div>
              );
            } else {
              return <Spin />;
            }
          } else if (isPdf) {
            return (
              <div
                style={{
                  position: "relative",
                  height: isFullscreen ? "100vh" : "77vh",
                  overflow: "auto",
                  background: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                }}
              >
                <Document
                  file={fileUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  loading={<Spin />}
                  error={<div>Failed to load PDF.</div>}
                >
                  <Page
                    pageNumber={currentPage}
                    scale={zoom}
                    width={isFullscreen ? window.innerWidth * 0.9 : 700}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </Document>
                {/* Controls as overlay */}
                <div
                  style={{
                    position: "sticky",
                    left: 0,
                    bottom: 0,
                    width: "100%",
                    background: "rgba(255,255,255,0.95)",
                    borderTop: "1px solid #eee",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 12,
                    padding: "8px 0",
                    zIndex: 2,
                  }}
                >
                  <Button
                    size="small"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    Prev
                  </Button>
                  <span>
                    Page {currentPage} of {numPages || "?"}
                  </span>
                  <Button
                    size="small"
                    disabled={numPages ? currentPage >= numPages : true}
                    onClick={() => setCurrentPage((p) => numPages ? Math.min(numPages, p + 1) : p)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            );
          } else if (isCsv) {
            return (
              <div style={{ maxHeight: isFullscreen ? "88vh" : "70vh", minWidth: "max-content" }}>
                {csvData ? (
                  <table style={{ borderCollapse: "collapse", minWidth: "max-content", width: "auto" }}>
                    <tbody>
                      {csvData.map((row, i) => (
                        <tr key={i}>
                          {row.map((cell, j) => (
                            <td key={j} style={{ border: "1px solid #eee", padding: 4, whiteSpace: "nowrap" }}>{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <Spin />}
              </div>
            );
          } else if (isJson) {
            return (
              <div style={{ overflowY: "auto", padding: 16 }}>
                {jsonParseError ? (
                  <div style={{
                    color: "#ff4d4f",
                    background: "#fff2f0",
                    border: "1px solid #ffccc7",
                    borderRadius: "4px",
                    padding: "12px",
                    marginBottom: "16px",
                  }}>
                    <strong>JSON Parse Error:</strong> {jsonParseError}
                    <br />
                    <small>The file contains invalid JSON format. Showing raw content below:</small>
                  </div>
                ) : null}
                {jsonData ? (
                  <div style={{ transform: `scale(${zoom})`, transformOrigin: "top left", transition: "transform 0.2s" }}>
                    <JSONTree
                      data={jsonData}
                      theme={getJsonTheme(selectedTheme)}
                      invertTheme={false}
                      shouldExpandNodeInitially={(keyPath: readonly any[], data: any, level: number) => level < 2}
                      labelRenderer={([key]) => <strong>{key}</strong>}
                    />
                  </div>
                ) : <Spin />}
              </div>
            );
          } else {
            return (
              <div style={{ textAlign: "center", padding: 32 }}>
                <p>
                  Preview not supported for this file type.<br />
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    href={fileUrl}
                    target="_blank"
                    download={fileName}
                    style={{ marginTop: 16 }}
                  >
                    Download {fileName}
                  </Button>
                </p>
              </div>
            );
          }
        })()}
      </div>
    </Modal>
  );
};

export default FilePreviewModal;